<template>
  <header class="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-40">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-16">
        <!-- 로고 및 네비게이션 -->
        <div class="flex items-center space-x-8">
          <!-- 로고 -->
          <RouterLink to="/" class="flex items-center space-x-2">
            <div class="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span class="text-white font-bold text-sm">S</span>
            </div>
            <span class="text-xl font-bold text-gray-900">Seatisfy</span>
          </RouterLink>
          
          <!-- 데스크톱 네비게이션 -->
          <nav class="hidden md:flex space-x-6">
            <RouterLink
              v-for="item in navigationItems"
              :key="item.path"
              :to="item.path"
              class="text-gray-600 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors"
              active-class="text-primary-600"
            >
              {{ item.name }}
            </RouterLink>
          </nav>
        </div>

        <!-- 사용자 메뉴 -->
        <div class="flex items-center space-x-4">
          <!-- 인증된 사용자 -->
          <div v-if="isAuthenticated" class="flex items-center space-x-4">
            <!-- 새 게시글 작성 버튼 -->
<!--            <RouteButton-->
<!--                route="/posts/create"-->
<!--                text="새 글 작성"-->
<!--            />-->

            <!-- 사용자 드롭다운 -->
            <Menu as="div" class="relative">
              <MenuButton class="flex items-center space-x-2 text-gray-700 hover:text-gray-900">
                <div class="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span class="text-primary-600 font-medium text-sm">
                    {{ user?.name?.charAt(0).toUpperCase() }}
                  </span>
                </div>
                <span class="hidden sm:inline text-sm font-medium text-gray-800 whitespace-nowrap">
                  {{ user?.name }}
                </span>
                <ChevronDownIcon class="w-4 h-4" />
              </MenuButton>

              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-1"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <MenuItems class="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div class="p-2">
                    <!-- 사용자 정보 -->
                    <div class="px-3 py-2 text-sm text-gray-900 font-medium border-b border-gray-100 mb-2">
                      {{ user?.name }}
                      <div class="text-xs text-gray-500">{{ user?.email }}</div>
                    </div>

                    <!-- 메뉴 항목들 -->
                    <MenuItem v-for="item in userMenuItems" :key="item.path" v-slot="{ active }">
                      <RouterLink
                        :to="item.path"
                        :class="[
                          active ? 'bg-gray-50' : '',
                          'flex items-center px-3 py-2 text-sm text-gray-700 rounded-md w-full'
                        ]"
                      >
                        <component :is="item.icon" class="w-4 h-4 mr-3" />
                        {{ item.name }}
                      </RouterLink>
                    </MenuItem>

                    <!-- 관리자 메뉴 (관리자인 경우) -->
                    <template v-if="isAdmin">
                      <div class="border-t border-gray-100 my-2"></div>
                      <MenuItem v-for="item in adminMenuItems" :key="item.path" v-slot="{ active }">
                        <RouterLink
                          :to="item.path"
                          :class="[
                            active ? 'bg-gray-50' : '',
                            'flex items-center px-3 py-2 text-sm text-gray-700 rounded-md w-full'
                          ]"
                        >
                          <component :is="item.icon" class="w-4 h-4 mr-3" />
                          {{ item.name }}
                        </RouterLink>
                      </MenuItem>
                    </template>

                    <!-- 로그아웃 -->
                    <div class="border-t border-gray-100 my-2"></div>
                    <MenuItem v-slot="{ active }">
                      <button
                        @click="handleLogout"
                        :class="[
                          active ? 'bg-gray-50' : '',
                          'flex items-center px-3 py-2 text-sm text-gray-700 rounded-md w-full text-left'
                        ]"
                      >
                        <ArrowRightOnRectangleIcon class="w-4 h-4 mr-3" />
                        로그아웃
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>

          <!-- 미인증 사용자 -->
          <div v-else class="flex items-center space-x-3">
            <RouteText
              route="/login"
              text="로그인"
            />
            <RouteButton
              route="/register"
              text="회원가입"
            />
          </div>

          <!-- 모바일 메뉴 버튼 -->
          <button
            @click="isMobileMenuOpen = !isMobileMenuOpen"
            class="md:hidden text-gray-600 hover:text-gray-900"
          >
            <Bars3Icon v-if="!isMobileMenuOpen" class="w-6 h-6" />
            <XMarkIcon v-else class="w-6 h-6" />
          </button>
        </div>
      </div>

      <!-- 모바일 네비게이션 -->
      <div v-if="isMobileMenuOpen" class="md:hidden border-t border-gray-200 py-4">
        <div class="space-y-2">
          <RouterLink
            v-for="item in navigationItems"
            :key="item.path"
            :to="item.path"
            @click="isMobileMenuOpen = false"
            class="block px-3 py-2 text-base font-medium text-gray-600 hover:text-primary-600"
            active-class="text-primary-600"
          >
            {{ item.name }}
          </RouterLink>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import {
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  DocumentTextIcon,
  CalendarIcon,
  UsersIcon,
  ChartBarIcon,
  BellIcon,
  QuestionMarkCircleIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import RouteText from "@/components/common/Button/RouteText.vue";
import RouteButton from "@/components/common/Button/RouteButton.vue";

const router = useRouter()
const authStore = useAuthStore()
const isMobileMenuOpen = ref(false)

// 인증 상태
const isAuthenticated = computed(() => authStore.isAuthenticated)
const isAdmin = computed(() => authStore.isAdmin)
const user = computed(() => authStore.user)

// 네비게이션 메뉴
const navigationItems = [
  { name: '예약', path: '/posts'},
  { name: '공지사항', path: '/notices'},
  { name: 'Q&A', path: '/qna'}
]

// 사용자 드롭다운 메뉴
const userMenuItems = [
  { name: '프로필', path: '/profile', icon: UserIcon },
  { name: '내 게시글', path: '/my/posts', icon: DocumentTextIcon },
  { name: '내 예약', path: '/my/reservations', icon: CalendarIcon }
]

// 관리자 메뉴
const adminMenuItems = [
  { name: '대시보드', path: '/admin', icon: ChartBarIcon },
  { name: '사용자 관리', path: '/admin/users', icon: UsersIcon }
]

const handleLogout = async () => {
  authStore.logout()
  router.push('/')
}

onBeforeMount(() => {
  if (authStore?.user?.role === 'ADMIN') {
    navigationItems.push({ name: '관리', path: '/admin'})
  }
})
</script>
